/* eslint-disable @typescript-eslint/no-explicit-any */
import { EditWorkoutVMDispatcher, MenuItems } from './workoutEdit.viewModel';
import { sprinkles } from '@design-system/sprinkles.css';
import {
  BuilderType,
  Circuit,
  LegacyExercise,
  LegacyTabata,
  Running,
  RunningExerciseSet,
  RunningExerciseType,
  RunningTempo,
  Template,
  Video,
  WorkoutItem,
} from '../models/workout.domain';
import {
  faRepeat,
  faVideo,
  faRunning,
  faDumbbell,
  faPhotoFilm,
  faStopwatch,
} from '@fortawesome/pro-solid-svg-icons';
import { Exercise as ExerciseEnum } from '@playbook-technologies/common-enums';
import { Tag } from 'generated/graphql';
import _ from 'lodash';
import uuid4 from 'uuid4';

export const getCurrentBreadcrumb = (type: BuilderType, workoutId?: number) => {
  const formType = workoutId ? 'Edit' : 'New';
  const builderType = type === 'class' ? 'Class' : 'Workout';
  return `${formType} ${builderType}`;
};

export const getTemplateSelection = (
  type: BuilderType,
  dispatch: EditWorkoutVMDispatcher,
) => {
  if (type === 'class') return [];
  const templates = [
    {
      label: 'Library',
      icon: faPhotoFilm,
      className: 'bg-black text-white',
      onClick: () => dispatch('openLibrary', { action: 'add' }),
    },
    {
      label: 'Exercise',
      icon: faDumbbell,
      className: 'bg-[#EFDFCC] text-[#CC7C1E]',
      onClick: () => dispatch('addTemplate', 'exercise'),
    },
    {
      label: 'Circuit',
      icon: faRepeat,
      className: 'bg-[#CCE0EF] text-[#246290]',
      onClick: () => dispatch('addTemplate', 'circuit'),
    },
    {
      label: 'Long Video',
      icon: faVideo,
      className: 'bg-[#EDE5FC] text-[#5F1ECC]',
      onClick: () => dispatch('addTemplate', 'video'),
    },
    {
      label: 'Autoplayed',
      icon: faStopwatch,
      className: 'bg-[#000] text-[#fff]',
      onClick: () =>
        dispatch('showAutoplayedSettingsModal', {
          exerciseId: undefined,
        }),
    },
    {
      label: 'Running',
      icon: faRunning,
      className: 'bg-[#C8EFCF] text-[#217D33]',
      onClick: () => dispatch('addTemplate', 'running'),
    },
  ];
  return templates;
};

const getMenuItems = (
  item: WorkoutItem,
  dispatch: EditWorkoutVMDispatcher,
  index: number,
  exercisesLength: number,
  builderType: BuilderType,
): MenuItems => {
  const items: MenuItems = [
    {
      label: 'Copy to clipboard',
      onClick: () => dispatch('addToClipboard', item),
      activeInReadonly: true,
    },
  ];

  if (index > 0) {
    items.push({
      label: 'Move up',
      onClick: () => dispatch('changeOrderInWorkout', { index, order: -1 }),
    });
  }
  if (index < exercisesLength - 1) {
    items.push({
      label: 'Move down',
      onClick: () => dispatch('changeOrderInWorkout', { index, order: 1 }),
    });
  }
  if (item.type === 11 || item.type === 10) {
    items.push({
      label: 'Edit format',
      onClick: () =>
        dispatch('showAutoplayedSettingsModal', {
          exerciseId: item.id,
        }),
    });
  }
  if (builderType !== 'class') {
    items.push({
      label: 'Remove',
      onClick: () =>
        dispatch('removeWorkoutItem', { index, exerciseId: item.uuid || '' }),
      className: sprinkles({ color: 'ERROR-100' }),
    });
  }

  return items;
};

const getCircuitMenuItems = ({
  item,
  dispatch,
  exerciseIndex,
  exercisesLength,
  circuitIndex,
}: {
  item: WorkoutItem;
  dispatch: EditWorkoutVMDispatcher;
  exerciseIndex: number;
  exercisesLength: number;
  circuitIndex: number;
}): MenuItems => {
  const items: MenuItems = [
    {
      label: 'Copy to clipboard',
      onClick: () => dispatch('addToClipboard', item),
    },
  ];

  if (exerciseIndex > 0) {
    items.push({
      label: 'Move up',
      onClick: () =>
        dispatch('changeOrderInCircuit', {
          circuitIndex,
          index: exerciseIndex,
          order: -1,
        }),
    });
  }
  if (exerciseIndex < exercisesLength - 1) {
    items.push({
      label: 'Move down',
      onClick: () =>
        dispatch('changeOrderInCircuit', {
          circuitIndex,
          index: exerciseIndex,
          order: 1,
        }),
    });
  }
  items.push({
    label: 'Remove',
    onClick: () =>
      dispatch('removeCircuitItem', { circuitIndex, exerciseIndex }),
    className: sprinkles({ color: 'ERROR-100' }),
  });
  return items;
};

export type CircuitTemplate = Omit<Circuit, 'exercises'> & {
  exercises: Array<{
    type: Template;
    header: { icon: IconProp; title: string };
    value: LegacyExercise;
    menuItems: MenuItems;
  }>;
};

type TemplateValue = (
  | LegacyExercise
  | Video
  | CircuitTemplate
  | LegacyTabata
  | Running<RunningExerciseType.Outdoor>
  | Running<RunningExerciseType.Treadmill>
) & { id?: string };

export type WorkoutItemTemplates = Array<{
  type: Template;
  header: { icon: IconProp; title: string };
  value: TemplateValue;
  menuItems: MenuItems;
}>;

export const exercisesToTemplates = (p: {
  exercises: WorkoutItem[];
  dispatch: EditWorkoutVMDispatcher;
  builderType: BuilderType;
}): WorkoutItemTemplates => {
  if (!p.exercises) return [];

  return p.exercises.map((item, index) => ({
    ...getExerciseTypeHeader(item.type),
    value: getTemplateValue(item, p.dispatch, index),
    menuItems: getMenuItems(
      item,
      p.dispatch,
      index,
      p.exercises.length,
      p.builderType,
    ),
  }));
};

const getTemplateValue = (
  item: WorkoutItem,
  dispatch: EditWorkoutVMDispatcher,
  index: number,
): TemplateValue => {
  if (item.type !== ExerciseEnum.Type.Multiple.value) {
    return item as
      | LegacyExercise
      | Video
      | LegacyTabata
      | Running<RunningExerciseType.Outdoor>
      | Running<RunningExerciseType.Treadmill>;
  }

  return mapCircuit(item as Circuit, dispatch, index);
};

const mapCircuit = (
  item: Circuit,
  dispatch: EditWorkoutVMDispatcher,
  circuitIndex: number,
): CircuitTemplate => {
  return {
    ...item,
    exercises: !item?.exercises
      ? []
      : item.exercises.map((exercise, i) => ({
          ...getExerciseTypeHeader(exercise.type),
          value: exercise,
          menuItems: getCircuitMenuItems({
            item: exercise,
            dispatch,
            circuitIndex,
            exercisesLength: item?.exercises?.length || 0,
            exerciseIndex: i,
          }),
        })),
  };
};

const getExerciseTypeHeader = (
  type: number,
): {
  type: Template;
  header: { icon: IconProp; title: string };
} => {
  switch (type) {
    case 11:
      return {
        type: 'HIIT' as Template,
        header: {
          icon: faStopwatch,
          title: 'HIIT',
        },
      };
    case 10:
      return {
        type: 'Tabata' as Template,
        header: {
          icon: faStopwatch,
          title: 'Tabata',
        },
      };
    case ExerciseEnum.Type.Multiple.value:
      return {
        type: 'circuit' as Template,
        header: {
          icon: faRepeat,
          title: 'Circuit title',
        },
      };
    case ExerciseEnum.Type.Single.value:
      return {
        type: 'exercise' as Template,
        header: {
          icon: faDumbbell,
          title: 'Exercise title',
        },
      };
    case ExerciseEnum.Type.YouTube.value:
      return {
        type: 'video' as Template,
        header: {
          icon: faVideo,
          title: 'Video title',
        },
      };

    case 100:
      return {
        type: 'running' as Template,
        header: {
          icon: faRunning,
          title: 'Running',
        },
      };

    default:
      return {
        type: 'exercise' as Template,
        header: {
          icon: faDumbbell,
          title: 'Exercise title',
        },
      };
  }
};

export type TagValidation = (values: Tag[], newValue: Tag) => Tag[];

export type TagOptions = Array<{
  category: string;
  values: Tag[];
  validation: TagValidation;
  max?: number;
  description: string;
}>;

export const mapTagsToOptions = (
  mappedData: Array<[string, Array<Tag>]>,
): TagOptions => {
  return mappedData.map(([category, values]) => ({
    category,
    values,
    validation: (values: Tag[], newValue: Tag) =>
      getTagValidationByCategory({ category, values, newValue }),
    max: getMaxSelection(category),
    description: getDescription(category),
  }));
};

export const getTagValidationByCategory = ({
  category,
  values,
  newValue,
}: {
  category: string;
  values: Tag[];
  newValue: Tag;
}): Tag[] => {
  const selectedCategory = values.filter((v) => v.category === category);
  const alreadySelected = !!_.find(values, ['id', newValue.id]);
  if (alreadySelected) return values.filter((v) => v.id !== newValue.id);

  if (values.length === maxTagsLimit) return values;

  const limit = maxTagSelectionByCategory[category];
  if (!limit) return [...values, newValue];

  if (limit == 1 && selectedCategory.length === limit)
    return [...values.filter((v) => v.category !== category), newValue];

  if (selectedCategory.length < limit) return [...values, newValue];

  return values;
};

const getMaxSelection = (category: string) => {
  switch (category) {
    case 'Category':
      return 3;
    case 'Location':
      return 1;
    case 'Difficulty':
      return 1;
    default:
      return;
  }
};

const getDescription = (category: string) => {
  switch (category) {
    case 'Category':
      return 'Select up to 3';
    case 'Location':
      return 'Select up to 1';
    case 'Difficulty':
      return 'Select up to 1';
    default:
      return 'Select up to 5';
  }
};

const maxTagSelectionByCategory: Record<string, number> = {
  Category: 3,
  Location: 1,
  Difficulty: 1,
};

export const maxTagsLimit = 10;

//
import { sum } from 'lodash';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export const isRunningExercise = (exercise: Circuit): boolean => {
  /** Exercise must have on of the following names */
  if (!['Running', 'Treadmill'].includes(exercise.name)) {
    return false;
  }

  /** Only type: 3 exercises can be a running-type exercise */
  if (exercise.type !== 3) {
    return false;
  }

  /**
   * Running exercise must have all set names consisting of 3 segments
   * @example "3.5 mi 40"
   */
  if (
    exercise.name === 'Running' &&
    exercise?.exercises &&
    exercise.exercises.some((exercise) => exercise.name.split(' ').length !== 3)
  ) {
    return false;
  }

  /**
   * Treadmill exercise must have all set names consisting of 4 segments
   * @example "3.5 mph 10 %"
   */
  if (
    exercise.name === 'Treadmill' &&
    exercise?.exercises &&
    exercise.exercises.some((exercise) => exercise.name.split(' ').length !== 4)
  ) {
    return false;
  }

  /** Every single set must follow this format to be parsed by the consumer app. */
  if (
    exercise?.exercises &&
    exercise.exercises.some(
      (exercise) =>
        !exercise.sets || !exercise.sets[0] || !exercise.sets[0].reps,
    )
  ) {
    return false;
  }

  return true;
};

export const toRunningTemplateData = (
  exercise: Circuit,
): Running<RunningExerciseType> | null => {
  if (!isRunningExercise(exercise)) {
    return null;
  }

  if (exercise.name === 'Running') {
    return {
      uuid: uuid4(),
      name: 'Running',
      type: 100,
      variant: RunningExerciseType.Outdoor,
      measurementMode: exercise.measurementMode || 'time-based',
      sets: !exercise?.exercises
        ? []
        : exercise.exercises.map((exercise) => {
            const [distance, _mile, tempo]: [string, 'mi', RunningTempo] =
              exercise.name.split(' ') as [string, 'mi', RunningTempo];
            const [time, timeUnit]: [string, 'sec' | 'min'] = exercise.sets[0]
              .reps as [string, 'sec' | 'min'];

            return {
              uuid: uuid4(),
              distance: Number(distance),
              tempo: Object.values(RunningTempo).includes(tempo)
                ? tempo
                : RunningTempo.Walk,
              audio: exercise.media || undefined,
              time: timeUnit === 'sec' ? Number(time) : Number(time) * 60,
            };
          }),
      media: exercise.media,
    };
  }
  const sets = !exercise?.exercises
    ? []
    : exercise.exercises.map((exercise) => {
        const [speed, _mph, incline]: [number, 'mph', number] =
          exercise.name.split(' ') as [number, 'mph', number];
        const [time, timeUnit]: [number, 'sec' | 'min'] = exercise.sets[0]
          .reps as [number, 'sec' | 'min'];

        return {
          uuid: uuid4(),
          speed: Number(speed),
          incline: Number(incline),
          audio: exercise.media || undefined,
          time: timeUnit === 'sec' ? time : time * 60,
        };
      });
  return {
    uuid: uuid4(),
    name: 'Running',
    type: 100,
    measurementMode: exercise.measurementMode || 'time-based',
    variant: RunningExerciseType.Treadmill,
    sets,
    media: exercise.media,
  };
};

export const runningTemplateToExercise = ({
  variant,
  sets,
  media,
  measurementMode,
}: Running<RunningExerciseType>): any => {
  if (variant === RunningExerciseType.Outdoor) {
    return {
      name: 'Running',
      type: 3,
      duration: [sum(sets.map((set) => set.time)), 'sec'],
      measurementMode: measurementMode || 'time-based',
      media,
      exercises: (
        sets as RunningExerciseSet[RunningExerciseType.Outdoor][]
      ).map(({ distance, time, tempo, audio: media }) => ({
        name: `${distance} mi ${tempo}`,
        rest: null,
        sets: [{ reps: [time, 'sec'] }],
        type: 2,
        description: '',
        media,
      })),
    };
  }

  return {
    name: 'Treadmill',
    type: 3,
    duration: [sum(sets.map((set) => set.time)), 'sec'],
    media: media,
    measurementMode: measurementMode || 'time-based',
    exercises: (
      sets as RunningExerciseSet[RunningExerciseType.Treadmill][]
    ).map(({ speed, time, incline, audio: media }) => ({
      name: `${speed} mph ${incline} %`,
      rest: null,
      sets: [{ reps: [time, 'sec'] }],
      type: 2,
      description: '',
      media,
    })),
  };
};

export const nameMatches = (t: string) => (v: { name: string }) => {
  return v.name.toLowerCase().includes(t.toLowerCase());
};
